/**
 * Application configuration (stores, languages, product lines) as exposed by
 * GET /api/config. Cached in localStorage under `app_config`, with TanStack
 * Query in front of it.
 */
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/shared/api/client';
import { STORAGE_KEYS } from '@/shared/constants';
import { local } from '@/shared/lib/storage';
import { productLineSchema } from './product-line';

export const storeSchema = z.object({
  id: z.union([z.string(), z.number()]),
  name: z.string(),
  region: z.string().nullish(),
  country: z.string().nullish(),
  workshopEventId: z.string().nullish(),
  workshopSavId: z.string().nullish(),
});

export type Store = z.infer<typeof storeSchema>;

export const appConfigSchema = z.object({
  stores: z.array(storeSchema).default([]),
  languages: z.array(z.string()).default([]),
  productLines: z.array(productLineSchema).optional(),
});

export type AppConfig = z.infer<typeof appConfigSchema>;

export const readCachedConfig = (): AppConfig | null => {
  const raw = local.getJson<unknown>(STORAGE_KEYS.APP_CONFIG);
  if (!raw) return null;
  const parsed = appConfigSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
};

export const writeCachedConfig = (config: AppConfig): void => {
  local.setJson(STORAGE_KEYS.APP_CONFIG, config);
};

export const fetchAppConfig = async (): Promise<AppConfig> => {
  const config = await api.get<AppConfig>('config', {
    schema: appConfigSchema,
  });
  writeCachedConfig(config);
  return config;
};

export const appConfigQueryKey = ['app-config'] as const;

export const appConfigQueryOptions = () =>
  queryOptions({
    queryKey: appConfigQueryKey,
    queryFn: fetchAppConfig,
    // Screens read the cached copy immediately, then refresh in the background.
    initialData: () => readCachedConfig() ?? undefined,
    initialDataUpdatedAt: 0,
    staleTime: 60_000,
  });

export type StoreId = Store['id'];

export const getSelectedStoreIds = (): StoreId[] => {
  const ids = local.getJson<unknown>(STORAGE_KEYS.SELECTED_STORES);
  return Array.isArray(ids)
    ? (ids.filter(
        (id) => typeof id === 'string' || typeof id === 'number',
      ) as StoreId[])
    : [];
};

export const setSelectedStoreIds = (ids: StoreId[]): void => {
  local.setJson(STORAGE_KEYS.SELECTED_STORES, ids);
};
